import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TokenService } from './token.service';
import {
  QUERY_WARRANTY_CERTIFICATES_BY_EQUIPMENT,
  QUERY_WARRANTY_CERTIFICATE,
} from '../graphql/queries/warranty-certificate';
import {
  MUTATION_CREATE_WARRANTY_CERTIFICATE_FOR_EQUIPMENT,
  MUTATION_UPSERT_WARRANTY_CERTIFICATE,
  MUTATION_DELETE_WARRANTY_CERTIFICATE,
  MUTATION_REPROCESS_SELECTED_WARRANTY_CERTIFICATES,
} from '../graphql/mutations/warranty-certificate';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class WarrantyCertificateService {
  private readonly apiUrl = environment.apiUrl;

  private readonly apollo = inject(Apollo);
  private readonly http = inject(HttpClient);
  private readonly tokenService = inject(TokenService);

  /**
   * Warranty certificates that apply to a unit — direct associations first,
   * then catalog make/model/model-year matches.
   */
  getWarrantyCertificatesByEquipment(equipmentId: string): Observable<any[]> {
    return this.apollo
      .query({
        query: QUERY_WARRANTY_CERTIFICATES_BY_EQUIPMENT,
        variables: { equipmentId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result: any) => result.data?.warrantyCertificatesByEquipment ?? []),
      );
  }

  /** Single certificate with the presigned PDF download URL. */
  getWarrantyCertificateById(id: string): Observable<any> {
    return this.apollo
      .query({
        query: QUERY_WARRANTY_CERTIFICATE,
        variables: { id },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result: any) => result.data?.warrantyCertificate));
  }

  /**
   * Create a certificate with a direct association to one unit (per-unit
   * upload flow from the equipment detail page).
   */
  createWarrantyCertificateForEquipment(
    input: any,
    equipmentId: string,
  ): Observable<any> {
    return this.apollo
      .mutate({
        mutation: MUTATION_CREATE_WARRANTY_CERTIFICATE_FOR_EQUIPMENT,
        variables: { input, equipmentId },
      })
      .pipe(
        map(
          (result: any) => result.data.createWarrantyCertificateForEquipment,
        ),
      );
  }

  upsertWarrantyCertificate(input: any): Observable<any> {
    return this.apollo
      .mutate({
        mutation: MUTATION_UPSERT_WARRANTY_CERTIFICATE,
        variables: { input },
      })
      .pipe(map((result: any) => result.data.upsertWarrantyCertificate));
  }

  deleteWarrantyCertificate(id: string): Observable<any> {
    return this.apollo
      .mutate({
        mutation: MUTATION_DELETE_WARRANTY_CERTIFICATE,
        variables: { id },
      })
      .pipe(map((result: any) => result.data.deleteWarrantyCertificate));
  }

  /** Re-queue the selected certificates for AI processing. */
  reprocessSelectedCertificates(
    warrantyCertificateIds: string[],
  ): Observable<{ requeuedCount: number; requeuedIds: string[] }> {
    return this.apollo
      .mutate({
        mutation: MUTATION_REPROCESS_SELECTED_WARRANTY_CERTIFICATES,
        variables: { warrantyCertificateIds },
      })
      .pipe(
        map(
          (result: any) => result.data.reprocessSelectedWarrantyCertificates,
        ),
      );
  }

  /**
   * Upload the certificate PDF via REST multipart. The API stores it in S3
   * and queues async AI processing (summary + auto-association).
   */
  uploadPdfMultipart(
    warrantyCertificateId: string,
    file: File,
  ): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('warrantyCertificateId', warrantyCertificateId);

    this.tokenService.getTokenFromStorage();
    const token = this.tokenService.token?.accessToken;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http.post(
      `${this.apiUrl}/warranty-certificate/upload-pdf`,
      formData,
      { headers },
    );
  }
}
